import { useCallback, useEffect, useRef, useState } from "react";
import { fetchBlogList } from "@/lib/blogApi";
import { consumeBlogBootstrap, invalidateBlogBootstrap, peekBlogBootstrap } from "@/lib/blogBootstrap";
import type { BlogCategory, BlogListItem } from "@/types/blog";

const PAGE_SIZE = 12;

type UseInfiniteBlogListOptions = {
  categorySlug?: string;
};

function matchingListBootstrap(categorySlug: string) {
  const boot = peekBlogBootstrap();
  if (boot?.type === "list" && boot.categorySlug === categorySlug) {
    return boot;
  }
  return null;
}

export function useInfiniteBlogList({ categorySlug = "" }: UseInfiniteBlogListOptions) {
  const seeded = matchingListBootstrap(categorySlug);
  const [items, setItems] = useState<BlogListItem[]>(seeded?.items ?? []);
  const [activeCategory, setActiveCategory] = useState<BlogCategory | undefined>(seeded?.category);
  const [page, setPage] = useState(seeded?.page ?? 1);
  const [hasMore, setHasMore] = useState(seeded?.hasMore ?? false);
  const [loading, setLoading] = useState(!seeded || Boolean(seeded.categoryNotFound));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [categoryNotFound, setCategoryNotFound] = useState(Boolean(seeded?.categoryNotFound));
  const [bootstrapCategories, setBootstrapCategories] = useState<BlogCategory[] | null>(
    seeded?.categories ?? null
  );

  const requestIdRef = useRef(0);
  const seededCategoryRef = useRef(seeded?.categorySlug);
  const skipNextFetchRef = useRef(Boolean(seeded && !seeded.categoryNotFound));

  const loadPage = useCallback(
    async (targetPage: number, mode: "replace" | "append") => {
      const requestId = ++requestIdRef.current;
      const isInitial = mode === "replace";

      if (isInitial) {
        setLoading(true);
        setError(false);
        setCategoryNotFound(false);
      } else {
        setLoadingMore(true);
      }

      const result = await fetchBlogList({
        page: targetPage,
        limit: PAGE_SIZE,
        category: categorySlug || undefined,
      });

      if (requestId !== requestIdRef.current) return;

      if (result.ok && result.data) {
        const data = result.data;

        if (data.categoryNotFound) {
          setCategoryNotFound(true);
          setItems([]);
          setHasMore(false);
          setActiveCategory(undefined);
          setPage(1);
        } else {
          setCategoryNotFound(false);
          setActiveCategory(data.category);
          setPage(targetPage);
          setHasMore(data.hasMore);

          if (mode === "replace") {
            setItems(data.items);
          } else {
            setItems((prev) => {
              const seen = new Set(prev.map((post) => post.id));
              const next = data.items.filter((post) => !seen.has(post.id));
              return [...prev, ...next];
            });
          }
        }
      } else {
        if (isInitial) {
          setItems([]);
          setHasMore(false);
          setActiveCategory(undefined);
          setError(true);
        }
      }

      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    },
    [categorySlug]
  );

  useEffect(() => {
    if (seededCategoryRef.current !== categorySlug) {
      seededCategoryRef.current = categorySlug;
      skipNextFetchRef.current = false;
      setBootstrapCategories(null);
    }

    const boot = consumeBlogBootstrap();
    if (boot?.type === "list" && boot.categorySlug === categorySlug) {
      setBootstrapCategories(boot.categories);
      if (boot.categoryNotFound) {
        setCategoryNotFound(true);
        setItems([]);
        setHasMore(false);
        setActiveCategory(undefined);
        setLoading(false);
        skipNextFetchRef.current = false;
        return;
      }

      setCategoryNotFound(false);
      setActiveCategory(boot.category);
      setItems(boot.items);
      setPage(boot.page);
      setHasMore(boot.hasMore);
      setLoading(false);
      setError(false);
      skipNextFetchRef.current = false;
      return;
    }

    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      setLoading(false);
      return;
    }

    invalidateBlogBootstrap();
    setPage(1);
    setHasMore(false);
    void loadPage(1, "replace");
  }, [categorySlug, loadPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore || categoryNotFound) return;
    void loadPage(page + 1, "append");
  }, [categoryNotFound, hasMore, loadPage, loading, loadingMore, page]);

  return {
    items,
    activeCategory,
    loading,
    loadingMore,
    error,
    categoryNotFound,
    hasMore,
    loadMore,
    bootstrapCategories,
  };
}
